import { ComponentProps, HTMLAttributes, MouseEvent, PropsWithChildren } from 'react'

import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

type PopoverMenuItemProps = HTMLAttributes<HTMLDivElement> & {
  isActive?: boolean
  isLoading?: boolean
  disabled?: boolean
}

function PopoverMenuItem({
  isActive = false,
  isLoading = false,
  disabled = false,
  onClick,
  children,
  className,
  ...props
}: PopoverMenuItemProps) {
  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (disabled || isLoading) return

    e.stopPropagation()
    onClick?.(e)
  }

  return (
    <div
      className={cn(
        'body-1 hover:bg-vive-primary-60 flex min-w-[144px] cursor-pointer items-center gap-2 rounded-sm p-2 transition-all hover:text-white',
        {
          'text-vive-brand-50': isActive,
          'cursor-wait': isLoading,
          'cursor-not-allowed': disabled,
        },
        className,
      )}
      onClick={handleClick}
      {...props}
    >
      {children}
    </div>
  )
}

type PopoverMenuItemsGroupProps = PropsWithChildren<{
  title?: string
}>

function PopoverMenuItemsGroup({ title, children }: PopoverMenuItemsGroupProps) {
  return (
    <div className="flex flex-col gap-2">
      {title && <div className="caption">{title}</div>}
      {children}
    </div>
  )
}

function PopoverMenuSeparator() {
  return <Separator className="bg-vive-primary-60 h-0.5" />
}

type PopoverMenuProps = PropsWithChildren<ComponentProps<typeof Card>>

export default function PopoverMenu({ className, children, ...props }: PopoverMenuProps) {
  return (
    <Card
      className={cn('bg-vive-primary-70 text-vive-primary-20 w-fit border-0 p-2', className)}
      {...props}
    >
      <CardContent className="flex flex-col gap-2 p-0">{children}</CardContent>
    </Card>
  )
}

PopoverMenu.Separator = PopoverMenuSeparator
PopoverMenu.Group = PopoverMenuItemsGroup
PopoverMenu.Item = PopoverMenuItem
